import React, { useEffect } from 'react';
import { 
  Plus, Edit, Trash, Download, Search, User, Eye, EyeOff, ChevronLeft, 
  ChevronRight, Upload, AlertCircle, CheckCircle, Loader, Copy, Pencil, Key
} from 'lucide-react';
import { useAccounts } from '../hooks/useAccounts';
import { useServices } from '../hooks/useServices';
import AccountWhatsappButton from '../AccountWhatsappButton'; // NUEVO IMPORT

const AccountsSection = ({ themes, currentTheme, services, clients }) => {
  const {
    accounts,
    loading,
    error,
    setError,
    showAccountForm,
    setShowAccountForm,
    showImportAccountsModal,
    setShowImportAccountsModal,
    showAssignProfileForm,    
    setShowAssignProfileForm,
    showEditProfileForm,      
    setShowEditProfileForm,
    importData,
    setImportData,
    importPreview,
    setImportPreview,
    importError,
    setImportError,
    importSuccess,
    setImportSuccess,
    newAccount,
    setNewAccount,
    profileAssignment,
    paginationInfo,
    setPaginationInfo,
    setProfileAssignment,
    editingProfile,
    setEditingProfile,
    currentAccountPage,
    itemsPerPage,
    searchTerm,
    setSearchTerm,
    clientSearchQuery,
    setClientSearchQuery,
    filteredClients,
    showClientResults,
    setShowClientResults,
    copySuccess,
    tooltipInfo,
    hiddenAccounts,
    showHiddenAccounts,
    setShowHiddenAccounts,
    serviceFilter,
    setServiceFilter,
    setCurrentAccountPage,
    expiryFilter,
    setExpiryFilter,
    fetchAccounts,
    handleSaveAccount,
    handleDeleteAccount,
    handleEditAccount,
    handleAssignProfile,
    handleEditProfile,
    handleSaveProfileAssignment,
    handleSaveEditedProfile,
    handleAccountsFileUpload,
    handleImportAccounts,
    handleAddProfile,
    handleProfileChange,
    handleServiceChange,
    isProfileNearExpiry,
    isProfileExpired,
    filterClients,
    handleClientSearch,
    handleSelectClient,
    getFilteredAccounts,
    getPaginatedAccounts,
    getServiceName,
    showProfileTooltip,
    hideProfileTooltip,
    getClientName,
    toggleHideAccount,
    copyProfileInfo,
    handleNextPage,
    handlePrevPage,
    handleItemsPerPageChange
  } = useAccounts();

  const { getServiceTemplate } = useServices();

  useEffect(() => {
    fetchAccounts(1, itemsPerPage);
  }, [searchTerm, serviceFilter, expiryFilter, showHiddenAccounts]);

  // Reset página a 1 cuando cambien los filtros
  useEffect(() => {
    setCurrentAccountPage(1);
  }, [searchTerm, serviceFilter, expiryFilter, showHiddenAccounts]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const renderTooltip = () => {
    if (!tooltipInfo.show) return null;
    
    return (
      <div 
        className="fixed z-50" 
        style={{ 
          left: `${tooltipInfo.position.x}px`, 
          top: `${tooltipInfo.position.y}px` 
        }}
      >
        {tooltipInfo.content}
      </div>
    );
  };

  if (loading && accounts.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader className="h-8 w-8 animate-spin mx-auto text-blue-500" />
          <p className={`mt-2 ${themes[currentTheme].textMuted}`}>Cargando cuentas...</p>
        </div>
      </div>
    );
  }

  if (error && accounts.length === 0) {
    return (
      <div className="p-6 bg-red-900 bg-opacity-25 rounded-lg">
        <p className="text-red-500">{error}</p>
        <button 
          className="mt-2 px-4 py-2 bg-blue-600 rounded-md text-white"
          onClick={() => {
            setError(null);
            fetchAccounts();
          }}
        >
          Intentar de nuevo
        </button>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className={`text-2xl font-bold ${themes[currentTheme].textPrimary}`}>Listado de Cuentas</h1>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className={`text-sm ${themes[currentTheme].textSecondary}`}>Mostrar cuentas ocultas</span>
            <div 
              className={`relative w-10 h-5 rounded-full cursor-pointer ${showHiddenAccounts ? 'bg-purple-600' : 'bg-gray-600'}`}
              onClick={() => setShowHiddenAccounts(!showHiddenAccounts)}
            >
              <div 
                className={`absolute top-0.5 left-0.5 bg-white w-4 h-4 rounded-full transition-transform ${
                  showHiddenAccounts ? 'transform translate-x-5' : ''
                }`}
              ></div>
            </div>
          </div>
          <div className="flex gap-2">
            <button 
              className="bg-blue-500 text-white px-4 py-2 rounded-md flex items-center gap-2"
              onClick={() => setShowImportAccountsModal(true)}
            >
              <Download size={20} /> Importar
            </button>
            <button 
              className="bg-green-500 text-white px-4 py-2 rounded-md flex items-center gap-2"
              onClick={() => {
                setNewAccount({ 
                  serviceId: services.length > 0 ? services[0].id : '', 
                  username: '', 
                  password: '', 
                  observation: '',
                  profiles: services.length > 0 ? 
                    Array.from({ length: services[0].max_profiles || services[0].maxProfiles || 1 }, 
                      (_, index) => ({ name: `Perfil ${index + 1}`, status:'libre' })) 
                    : [{ name: '', status: 'libre' }]
                });
                setShowAccountForm(true);
              }}
            >
              <Plus size={20} /> Nueva
            </button>
          </div>
        </div>
      </div>
      
      <div className="mb-6 flex flex-wrap gap-4">
        <div className="flex-1 min-w-[300px]">
          <div className={`${themes[currentTheme].bgSecondary} p-4 rounded-lg`}>
            <div className="flex flex-wrap gap-4">
              <div className="flex-grow">
                <label className={`block text-sm font-medium ${themes[currentTheme].textSecondary} mb-1`}>Buscar</label>
                <div className="relative">
                  <Search size={20} className={`absolute left-3 top-2.5 ${themes[currentTheme].textMuted}`} />
                  <input
                    type="text"
                    placeholder="Buscar por servicio, correo o observaciones..."
                    className={`w-full pl-10 p-2 rounded-md ${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary}`}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              <div>
                <label className={`block text-sm font-medium ${themes[currentTheme].textSecondary} mb-1`}>Filtrar por servicio</label>
                <select 
                  className={`w-full p-2 rounded-md ${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary}`}
                  value={serviceFilter}
                  onChange={(e) => setServiceFilter(e.target.value)}
                >
                  <option value="">Todos los servicios</option>
                  {services.map(service => (
                    <option key={service.id} value={service.id}>{service.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={`block text-sm font-medium ${themes[currentTheme].textSecondary} mb-1`}>Estado</label>
                <select 
                  className={`w-full p-2 rounded-md ${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary}`}
                  value={expiryFilter}
                  onChange={(e) => setExpiryFilter(e.target.value)}
                >
                  <option value="all">Todos los perfiles</option>
                  <option value="disponibles">Disponibles</option>
                  <option value="ocupados">Ocupados</option>
                  <option value="expired">Perfiles expirados</option>
                  <option value="nearExpiry">Próximos a expirar</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {accounts.length === 0 ? (
        <div className={`${themes[currentTheme].bgSecondary} p-8 rounded-lg text-center`}>
          <p className={themes[currentTheme].textMuted}>No hay cuentas registradas.</p>
          <button 
            className="mt-4 px-4 py-2 bg-blue-600 rounded-md text-white"
            onClick={() => {
              setNewAccount({ 
                serviceId: services.length > 0 ? services[0].id : '', 
                username: '', 
                password: '', 
                observation: '',
                profiles: services.length > 0 ? 
                  Array.from({ length: services[0].max_profiles || services[0].maxProfiles || 1 }, 
                    (_, index) => ({ name: `Perfil ${index + 1}`, status: 'libre' })) 
                  : [{ name: '', status: 'libre' }]
              });
              setShowAccountForm(true);
            }}
          >
            Crear la primera cuenta
          </button>
        </div>
      ) : (
        <div className={`${themes[currentTheme].bgSecondary} rounded-lg overflow-hidden`}>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-800">
              <thead className={themes[currentTheme].bgPrimary}>
                <tr>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${themes[currentTheme].textSecondary} uppercase tracking-wider`}>#</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${themes[currentTheme].textSecondary} uppercase tracking-wider`}>Cuenta</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${themes[currentTheme].textSecondary} uppercase tracking-wider`}>Contraseña</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${themes[currentTheme].textSecondary} uppercase tracking-wider`}>Observación</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${themes[currentTheme].textSecondary} uppercase tracking-wider`}>Perfiles</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${themes[currentTheme].textSecondary} uppercase tracking-wider`}>Acciones</th>
                </tr>
              </thead>
              <tbody className={`${themes[currentTheme].bgSecondary} divide-y divide-gray-800`}>
              {getPaginatedAccounts(services).map((account, index) => {
                const displayIndex = (currentAccountPage - 1) * itemsPerPage + index + 1;
                
                return (
                  <tr key={account.id}>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${themes[currentTheme].textSecondary}`}>{displayIndex}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {(() => {
                          const service = services.find(s => s.id === parseInt(account.service_id || account.serviceId));
                          return (
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10">
                                <img 
                                  className="h-10 w-10 rounded-full" 
                                  src={service?.image || 'https://via.placeholder.com/48'} 
                                  alt="" 
                                />
                              </div>
                              <div className="ml-3 flex flex-col">
                                <span className={`text-sm font-medium ${themes[currentTheme].textPrimary}`}>
                                  {service?.name || 'N/A'}
                                </span>
                                <span className={`text-xs ${themes[currentTheme].textMuted}`}>
                                  {account.username}
                                </span>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${themes[currentTheme].textSecondary}`}>{account.password}</td>
                    <td className={`px-6 py-4 text-sm ${themes[currentTheme].textSecondary}`}>
                      {account.observation ? (
                        <div className="relative group max-w-xs">
                          <div className="truncate cursor-pointer" title={account.observation}>
                            {account.observation}
                          </div>
                          {/* Tooltip que aparece al pasar el mouse */}
                          <div className="absolute left-0 top-0 mt-6 w-64 bg-gray-800 p-2 rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity z-10">
                            <p className="text-xs text-white whitespace-pre-wrap break-words">
                              {account.observation}
                            </p>
                          </div>
                        </div>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className={`px-6 py-4 text-sm ${themes[currentTheme].textSecondary}`}>
                      <div className="flex flex-wrap gap-2">
                        {account.profiles && account.profiles.map((profile) => {
                          let status = profile.status;
                          let profileColor = 'text-white';
                          let bgColor = 'bg-gray-700 bg-opacity-50';
                          
                          if (status === 'ocupado') {
                            if (isProfileExpired(profile)) {
                              status = 'expirado';
                              profileColor = 'text-red-400';
                              bgColor = 'bg-red-900 bg-opacity-30';
                            } else if (isProfileNearExpiry(profile)) {
                              profileColor = 'text-orange-400';
                              bgColor = 'bg-orange-900 bg-opacity-30';
                            } else {
                              profileColor = 'text-green-400';
                              bgColor = 'bg-green-900 bg-opacity-30';
                            }
                          } else if (status === 'expirado') {
                            profileColor = 'text-red-400';
                            bgColor = 'bg-red-900 bg-opacity-30';
                          }
                          
                          return (
                            <div 
                              key={profile.id} 
                              className={`flex flex-col items-center p-2 rounded-md cursor-pointer ${bgColor}`}
                              onClick={() => {
                                if (profile.status === 'libre') {
                                  handleAssignProfile(account, profile, services);
                                } else if (profile.status === 'ocupado') {
                                  handleEditProfile(account, profile);
                                } else {
                                  alert(`Este perfil está ${profile.status}. No se puede asignar.`);
                                }
                              }}
                              onMouseEnter={(e) => showProfileTooltip(e, profile, account, getServiceName(account.service_id || account.serviceId, services), clients)}
                              onMouseLeave={hideProfileTooltip}
                            >
                              <User size={24} className={profileColor} />
                            </div>
                          );
                        })}
                      </div>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${themes[currentTheme].textSecondary} flex gap-2`}>
                      {/* NUEVO BOTÓN DE WHATSAPP */}
                      <AccountWhatsappButton 
                        account={account}
                        services={services}
                        clients={clients}
                      />
                      
                      <button 
                        className="text-blue-400 hover:text-blue-500"
                        onClick={() => handleEditAccount(account)}
                      >
                        <Edit size={16} />
                      </button>
                      <button 
                        className="text-red-400 hover:text-red-500"
                        onClick={() => handleDeleteAccount(account.id)}
                      >
                        <Trash size={16} />
                      </button>
                      <button 
                        className={`${hiddenAccounts.includes(account.id) ? 'text-purple-400 hover:text-purple-500' : 'text-gray-400 hover:text-gray-300'}`}
                        onClick={() => toggleHideAccount(account.id)}
                        title={hiddenAccounts.includes(account.id) ? "Mostrar cuenta" : "Ocultar cuenta"}
                      >
                        {hiddenAccounts.includes(account.id) ? <Eye size={16} /> : <EyeOff size={16} />}
                      </button>
                    </td>
                  </tr>
                );
              })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      {/* Paginación para cuentas */}
      <div className="mt-4 flex items-center justify-between">
        <div className="flex items-center">
          <span className={`mr-2 text-sm ${themes[currentTheme].textMuted}`}>Filas por página:</span>
          <select 
            className={`${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary} rounded-md px-2 py-1 text-sm`}
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
          </select>
        </div>
        <div className="flex items-center">
          <span className={`mr-4 text-sm ${themes[currentTheme].textMuted}`}>
            {paginationInfo.totalCount > 0 
              ? `${(paginationInfo.currentPage - 1) * paginationInfo.limit + 1}-${Math.min(paginationInfo.currentPage * paginationInfo.limit, paginationInfo.totalCount)} de ${paginationInfo.totalCount}` 
              : '0-0 de 0'}
          </span>
          <div className="flex space-x-1">
            <button 
              className={`p-1 rounded-md hover:${themes[currentTheme].bgTertiary} ${paginationInfo.currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={handlePrevPage}
              disabled={paginationInfo.currentPage === 1}
            >
              <ChevronLeft size={20} className={themes[currentTheme].textMuted} />
            </button>
            <button 
              className={`p-1 rounded-md hover:${themes[currentTheme].bgTertiary} ${paginationInfo.currentPage >= paginationInfo.totalPages ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={handleNextPage}
              disabled={paginationInfo.currentPage >= paginationInfo.totalPages}
            >
              <ChevronRight size={20} className={themes[currentTheme].textMuted} />
            </button>
          </div>
        </div>
      </div>
      
      {/* Modal para formulario de cuenta */}
      {showAccountForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${themes[currentTheme].bgSecondary} p-6 rounded-lg w-full max-w-md max-h-screen overflow-y-auto`}>
            <h2 className={`text-xl font-bold mb-4 ${themes[currentTheme].textPrimary}`}>
              {newAccount.id ? 'Editar Cuenta' : 'Nueva Cuenta'}
            </h2>
            <form className="space-y-4">
              <div>
                <label className={`block text-sm font-medium ${themes[currentTheme].textSecondary} mb-1`}>Servicio</label>
                <select 
                  className={`w-full p-2 rounded-md ${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary}`}
                  value={newAccount.serviceId}
                  onChange={(e) => handleServiceChange(e.target.value, services)}
                >
                  {services.length === 0 ? (
                    <option value="">No hay servicios disponibles</option>
                  ) : (
                    services.map(service => (
                      <option key={service.id} value={service.id}>{service.name}</option>
                    ))
                  )}
                </select>
              </div>
              <div>
                <label className={`block text-sm font-medium ${themes[currentTheme].textSecondary} mb-1`}>Cuenta</label>
                <input 
                  type="text" 
                  className={`w-full p-2 rounded-md ${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary}`}
                  value={newAccount.username}
                  onChange={(e) => setNewAccount({ ...newAccount, username: e.target.value })}
                />
                <p className={`text-xs ${themes[currentTheme].textMuted} mt-1`}>Correo electrónico o nombre de usuario para acceder al servicio</p>
              </div>
              <div>
                <label className={`block text-sm font-medium ${themes[currentTheme].textSecondary} mb-1`}>Contraseña</label>
                <input 
                  type="text" 
                  className={`w-full p-2 rounded-md ${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary}`} 
                  value={newAccount.password}
                  onChange={(e) => setNewAccount({ ...newAccount, password: e.target.value })}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium ${themes[currentTheme].textSecondary} mb-1`}>Observación</label>
                <textarea 
                  className={`w-full p-2 rounded-md ${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary}`}
                  rows="2"
                  placeholder="Información adicional sobre esta cuenta..."
                  value={newAccount.observation}
                  onChange={(e) => setNewAccount({ ...newAccount, observation: e.target.value })}
                ></textarea>
              </div>
              <div>
                <label className={`block text-sm font-medium ${themes[currentTheme].textSecondary} mb-1`}>Perfiles</label>
                <div className="space-y-2">
                  {newAccount.profiles.map((profile, index) => (
                    <div key={index} className="grid grid-cols-3 gap-2">
                      <input 
                        type="text" 
                        className={`p-2 text-sm rounded-md ${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary}`} 
                        placeholder="Nombre del perfil"
                        value={profile.name}
                        onChange={(e) => handleProfileChange(index, 'name', e.target.value)}
                      />
                      <select 
                        className={`p-2 text-sm rounded-md ${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary}`}
                        value={profile.status}
                        onChange={(e) => handleProfileChange(index, 'status', e.target.value)}
                      >
                        <option value="libre">Libre</option>
                        <option value="ocupado">Ocupado</option>
                        <option value="expirado">Expirado</option>
                      </select>
                      {profile.status === 'ocupado' ? (
                        <select 
                          className={`p-2 text-sm rounded-md ${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary}`}
                          value={profile.clientId || ''}
                          onChange={(e) => handleProfileChange(index, 'clientId', e.target.value || null)}
                        >
                          <option value="">Seleccionar cliente</option>
                          {clients.map(client => (
                            <option key={client.id} value={client.id}>{client.name}</option>
                          ))}
                        </select>
                      ) : (
                        <div className={`p-2 text-sm rounded-md ${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textMuted} flex items-center justify-center`}>
                          -
                        </div>
                      )}
                    </div>
                  ))}
                  <button 
                    type="button" 
                    className="text-sm text-blue-400 flex items-center gap-1"
                    onClick={handleAddProfile}
                  >
                    <Plus size={16} /> Agregar perfil
                  </button>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <button 
                  type="button" 
                  className={`px-4 py-2 border ${themes[currentTheme].borderColor} rounded-md ${themes[currentTheme].textSecondary}`}
                  onClick={() => {
                    setShowAccountForm(false);
                    if (newAccount.id) {
                      setNewAccount({ serviceId: '', username: '', password: '', observation: '', profiles: [{ name: '', status: 'libre' }] });
                    }
                  }}
                >
                  Cancelar
                </button>
                <button 
                  type="button" 
                  className="px-4 py-2 bg-blue-600 rounded-md text-white"
                  onClick={handleSaveAccount}
                  disabled={loading}
                >
                  {loading ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal para asignar perfil a un cliente */}
      {showAssignProfileForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${themes[currentTheme].bgSecondary} p-6 rounded-lg w-full max-w-md`}>
            <h2 className={`text-xl font-bold mb-4 ${themes[currentTheme].textPrimary}`}>Asignar Perfil a Cliente</h2>
            
            <div className={`${themes[currentTheme].bgTertiary} p-4 rounded-md mb-4`}>
              <p className={`text-sm ${themes[currentTheme].textMuted}`}>Servicio: <span className={themes[currentTheme].textPrimary}>{profileAssignment.serviceName}</span></p>
              <p className={`text-sm ${themes[currentTheme].textMuted} mt-1`}>Perfil: <span className={themes[currentTheme].textPrimary}>{profileAssignment.profileName}</span></p>
              <p className={`text-sm ${themes[currentTheme].textMuted} mt-1`}>Precio: <span className={themes[currentTheme].textPrimary}>{formatCurrency(profileAssignment.servicePrice)}</span></p>
            </div>
            
            <form className="space-y-4">
              <div>
                <label className={`block text-sm font-medium ${themes[currentTheme].textSecondary} mb-1`}>Cliente</label>
                <div className="relative">
                  <input 
                    type="text" 
                    className={`w-full p-2 rounded-md ${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary}`}
                    placeholder="Buscar cliente por nombre o teléfono..."
                    value={clientSearchQuery}
                    onChange={(e) => handleClientSearch(e, clients)}
                    onFocus={() => filterClients(clientSearchQuery, clients)}
                  />
                  
                  {showClientResults && filteredClients.length > 0 && (
                    <div className={`absolute z-10 mt-1 w-full ${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} rounded-md shadow-lg max-h-60 overflow-auto`}>
                      {filteredClients.map(client => (
                        <div 
                          key={client.id}
                          className={`p-2 cursor-pointer hover:${themes[currentTheme].bgPrimary} ${
                            profileAssignment.clientId === client.id ? themes[currentTheme].bgPrimary : ''
                          }`}
                          onClick={() => handleSelectClient(client)}
                        >
                          <div className="flex justify-between">
                            <div>
                              <span className={themes[currentTheme].textPrimary}>{client.name}</span>
                              {client.type === 'revendedor' && (
                                <span className="ml-2 px-1 py-0.5 bg-purple-900 text-purple-300 text-xs rounded-full">
                                  Revendedor
                                </span>
                              )}
                            </div>
                            {client.phone && (
                              <span className={themes[currentTheme].textMuted}>{client.phone}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {!profileAssignment.clientId && (
                  <p className="text-xs text-red-400 mt-1">
                    Selecciona un cliente para asignar el perfil
                  </p>
                )}
              </div>
              
              {profileAssignment.clientId && (
                <div className={`${themes[currentTheme].bgTertiary} p-3 rounded-md`}>
                  <p className={`text-sm font-medium ${themes[currentTheme].textSecondary}`}>Cliente seleccionado:</p>
                  <div className={`mt-1 p-2 ${themes[currentTheme].bgPrimary} rounded`}>
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-medium">
                          {getClientName(profileAssignment.clientId, clients)}
                          {clients.find(c => c.id === parseInt(profileAssignment.clientId))?.type === 'revendedor' && (
                            <span className="ml-2 px-1 py-0.5 bg-purple-900 text-purple-300 text-xs rounded-full">
                              Revendedor
                            </span>
                          )}
                        </p>
                        {clients.find(c => c.id === parseInt(profileAssignment.clientId))?.phone && (
                          <p className={`text-xs ${themes[currentTheme].textMuted}`}>
                            {clients.find(c => c.id === parseInt(profileAssignment.clientId))?.phone}
                          </p>
                        )}
                      </div>
                      <button 
                        onClick={() => {
                          setProfileAssignment({...profileAssignment, clientId: ''});
                          setClientSearchQuery('');
                        }}
                        className="text-xs text-red-400 hover:text-red-300"
                      >
                        Cambiar
                      </button>
                    </div>
                  </div>
                </div>
              )}
              
              <div>
                <label className={`block text-sm font-medium ${themes[currentTheme].textSecondary} mb-1`}>PIN</label>
                <input 
                  type="text" 
                  className={`w-full p-2 rounded-md ${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary}`}
                  value={profileAssignment.pin}
                  onChange={(e) => setProfileAssignment({ ...profileAssignment, pin: e.target.value })}
                  maxLength="5"
                  placeholder="Ingrese un PIN numérico"
                />
              </div>
              
              <div>
                <label className={`block text-sm font-medium ${themes[currentTheme].textSecondary} mb-1`}>Fecha de Vencimiento</label>
                <input 
                  type="date" 
                  className={`w-full p-2 rounded-md ${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary}`}
                  value={profileAssignment.expiryDate}
                  onChange={(e) => setProfileAssignment({ ...profileAssignment, expiryDate: e.target.value })}
                />
              </div>
              
              <div className="flex justify-end gap-2 pt-4">
                <button 
                  type="button" 
                  className={`px-4 py-2 border ${themes[currentTheme].borderColor} rounded-md ${themes[currentTheme].textSecondary}`}
                  onClick={() => setShowAssignProfileForm(false)}
                >
                  Cancelar
                </button>
                <button 
                  type="button" 
                  className="px-4 py-2 bg-green-600 rounded-md text-white flex items-center gap-2"
                  onClick={() => handleSaveProfileAssignment(services, clients, null, getServiceTemplate)}
                  disabled={loading}
                >
                  {loading ? <><Loader size={16} className="animate-spin" /> Guardando...</> : 
                  copySuccess ? <><CheckCircle size={16} /> Copiado</> : <><Copy size={16} /> Guardar y Copiar</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Modal para editar perfil existente */}
      {showEditProfileForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${themes[currentTheme].bgSecondary} p-6 rounded-lg w-full max-w-md`}>
            <h2 className={`text-xl font-bold mb-4 ${themes[currentTheme].textPrimary}`}>Editar Perfil</h2>
            
            <form className="space-y-4">
              <div>
                <label className={`block text-sm font-medium ${themes[currentTheme].textSecondary} mb-1`}>Nombre</label>
                <input 
                  type="text" 
                  className={`w-full p-2 rounded-md ${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary}`}
                  value={editingProfile.name}
                  onChange={(e) => setEditingProfile({ ...editingProfile, name: e.target.value })}
                />
              </div>
              
              <div>
                <label className={`block text-sm font-medium ${themes[currentTheme].textSecondary} mb-1`}>Estado</label>
                <select 
                  className={`w-full p-2 rounded-md ${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary}`}
                  value={editingProfile.status}
                  onChange={(e) => setEditingProfile({ ...editingProfile, status: e.target.value })}
                >
                  <option value="libre">Libre</option>
                  <option value="ocupado">Ocupado</option>
                  <option value="expirado">Expirado</option>
                </select>
              </div>
              
              {editingProfile.status === 'ocupado' && (
                <>
                  <div>
                    <label className={`block text-sm font-medium ${themes[currentTheme].textSecondary} mb-1`}>Cliente</label>
                    <select 
                      className={`w-full p-2 rounded-md ${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary}`}
                      value={editingProfile.clientId}
                      onChange={(e) => setEditingProfile({ ...editingProfile, clientId: e.target.value })}
                    >
                      <option value="">Seleccionar cliente</option>
                      {clients.map(client => (
                        <option key={client.id} value={client.id}>{client.name}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className={`${themes[currentTheme].bgTertiary} p-3 rounded-md`}>
                    <p className={`text-sm font-medium ${themes[currentTheme].textSecondary}`}>Cliente seleccionado:</p>
                    <div className={`mt-1 p-2 ${themes[currentTheme].bgPrimary} rounded`}>
                      <p className="font-medium">{getClientName(editingProfile.clientId, clients)}</p>
                      {clients.find(c => c.id === parseInt(editingProfile.clientId))?.phone && (
                        <p className={`text-xs ${themes[currentTheme].textMuted}`}>{clients.find(c => c.id === parseInt(editingProfile.clientId))?.phone}</p>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <label className={`block text-sm font-medium ${themes[currentTheme].textSecondary} mb-1`}>PIN</label>
                    <input 
                      type="text" 
                      className={`w-full p-2 rounded-md ${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary}`}
                      value={editingProfile.pin}
                      onChange={(e) => setEditingProfile({ ...editingProfile, pin: e.target.value })}
                      maxLength="5"
                      placeholder="Ingrese un PIN de 4/5 dígitos"
                    />
                  </div>
                  
                  <div>
                    <label className={`block text-sm font-medium ${themes[currentTheme].textSecondary} mb-1`}>Fecha de Vencimiento</label>
                    <input 
                      type="date" 
                      className={`w-full p-2 rounded-md ${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary}`}
                      value={editingProfile.expiryDate}
                      onChange={(e) => setEditingProfile({ ...editingProfile, expiryDate: e.target.value })}
                    />
                  </div>
                </>
              )}
              
              <div className="flex justify-between pt-4">
                <button 
                  type="button" 
                  className="px-4 py-2 bg-purple-600 rounded-md text-white flex items-center gap-2"
                  onClick={() => copyProfileInfo(services, getServiceTemplate)}
                  disabled={loading}
                >
                  {copySuccess ? 
                    <><CheckCircle size={16} /> Copiado</> : 
                    <><Copy size={16} /> Copiar Info</>}
                </button>
                <div className="flex gap-2">
                  <button 
                    type="button" 
                    className={`px-4 py-2 border ${themes[currentTheme].borderColor} rounded-md ${themes[currentTheme].textSecondary}`}
                    onClick={() => setShowEditProfileForm(false)}
                  >
                    Cancelar
                  </button>
                  <button 
                    type="button" 
                    className="px-4 py-2 bg-blue-600 rounded-md text-white"
                    onClick={handleSaveEditedProfile}
                    disabled={loading}
                  >
                    {loading ? <><Loader size={16} className="animate-spin mr-1" /> Guardando...</> : 'Guardar Cambios'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Modal para importación de cuentas */}
      {showImportAccountsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${themes[currentTheme].bgSecondary} p-6 rounded-lg w-full max-w-3xl max-h-[80vh] overflow-y-auto`}>
            <h2 className={`text-xl font-bold mb-4 ${themes[currentTheme].textPrimary} flex items-center`}>
              <Download size={20} className="mr-2 text-blue-400" /> Importar Cuentas
            </h2>
            
            <div className="mb-4">
              <p className={`text-sm ${themes[currentTheme].textSecondary} mb-2`}>
                Seleccione un archivo JSON con la lista de cuentas y perfiles. Se importarán los datos de la cuenta, incluyendo email, contraseña, y la información de los perfiles y clientes asociados.
              </p>
              
              <div className="flex items-center gap-2 mt-4">
                <input 
                  type="file" 
                  accept=".json" 
                  onChange={handleAccountsFileUpload}
                  className="hidden" 
                  id="account-file-input" 
                />
                <label 
                  htmlFor="account-file-input"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md cursor-pointer flex items-center gap-2"
                >
                  <Upload size={16} /> Seleccionar archivo
                </label>
                {importData && (
                  <span className={`text-sm ${themes[currentTheme].textMuted}`}>
                    {importData.length} cuenta(s) encontrada(s)
                  </span>
                )}
              </div>
            </div>
            
            {importError && (
              <div className="bg-red-900 bg-opacity-25 p-3 rounded-md mb-4 flex items-start">
                <AlertCircle className="text-red-500 mr-2 mt-0.5" size={16} />
                <p className="text-sm text-red-500">{importError}</p>
              </div>
            )}
            
            {importSuccess && (
              <div className="bg-green-900 bg-opacity-25 p-3 rounded-md mb-4 flex items-start">
                <CheckCircle className="text-green-500 mr-2 mt-0.5" size={16} />
                <p className="text-sm text-green-500">{importSuccess}</p>
              </div>
            )}
            
            {importPreview.length > 0 && (
              <div className="mb-4">
                <h3 className={`text-md font-semibold mb-2 ${themes[currentTheme].textPrimary}`}>
                  Vista previa:
                </h3>
                <div className={`${themes[currentTheme].bgTertiary} p-4 rounded-md max-h-64 overflow-y-auto`}>
                  {importPreview.map((account, index) => (
                    <div key={index} className="mb-4 border-b border-gray-700 pb-4">
                      <div className="flex items-center gap-2 mb-3">
                        {account.product && account.product.image_url && (
                          <img src={account.product.image_url} alt={account.product.name} className="w-10 h-10 rounded-full" />
                        )}
                        <div>
                          <p className={`text-sm font-medium ${themes[currentTheme].textPrimary}`}>
                            {account.product ? account.product.name : 'Servicio sin nombre'}
                          </p>
                          <p className={`text-xs ${themes[currentTheme].textMuted}`}>
                            Email: {account.email || 'N/A'} | Contraseña: {account.service_password || 'N/A'}
                          </p>
                        </div>
                      </div>
                      
                      <div className="ml-4">
                        <p className={`text-xs font-medium mb-2 ${themes[currentTheme].textSecondary}`}>
                          Perfiles ({account.account_customer && Array.isArray(account.account_customer) ? 
                            account.account_customer.filter(p => p).length : 0} 
                            ocupados de {account.product ? account.product.max_profiles || 0 : 0}):
                        </p>
                        
                        {account.account_customer && Array.isArray(account.account_customer) ? 
                          account.account_customer.filter(p => p).map((profile, pidx) => (
                            <div key={pidx} className="ml-2 mb-2 p-2 bg-gray-800 rounded-md">
                              <div className="flex justify-between items-start">
                                <div>
                                  <p className={`text-xs ${themes[currentTheme].textPrimary} font-medium`}>
                                    Perfil {profile.line_num || pidx + 1}: {profile.profile_name || 'Sin nombre'} 
                                  </p>
                                  <p className={`text-xs ${themes[currentTheme].textMuted}`}>
                                    PIN: {profile.pin || 'N/A'} | Vence: {profile.end_date ? new Date(profile.end_date).toLocaleDateString() : 'Sin fecha'}
                                  </p>
                                </div>
                                
                                {profile.customer && (
                                  <div className="bg-blue-900 bg-opacity-30 px-2 py-1 rounded-md">
                                    <p className={`text-xs ${themes[currentTheme].textPrimary}`}>
                                      Cliente: {profile.customer.name}
                                    </p>
                                    <p className={`text-xs ${themes[currentTheme].textMuted}`}>
                                      Tel: {profile.customer.cellphone || profile.customer.cellphone_formatted || 'N/A'}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                          )) : 
                          <p className={`text-xs ${themes[currentTheme].textMuted}`}>No hay perfiles disponibles</p>
                        }
                      </div>
                    </div>
                  ))}
                  {importData.length > importPreview.length && (
                    <p className={`text-xs ${themes[currentTheme].textMuted} mt-2`}>
                      ...y {importData.length - importPreview.length} cuenta(s) más
                    </p>
                  )}
                </div>
              </div>
            )}
            
            <div className="flex justify-end gap-2 pt-4">
              <button 
                type="button" 
                className={`px-4 py-2 border ${themes[currentTheme].borderColor} rounded-md ${themes[currentTheme].textSecondary}`}
                onClick={() => {
                  setShowImportAccountsModal(false);
                  setImportData(null);
                  setImportPreview([]);
                  setImportError('');
                  setImportSuccess('');
                }}
              >
                Cancelar
              </button>
              <button 
                type="button" 
                className="px-4 py-2 bg-green-600 rounded-md text-white flex items-center gap-2"
                onClick={() => handleImportAccounts(services, clients)}
                disabled={loading || !importData}
              >
                {loading ? (
                  <>
                    <Loader size={16} className="animate-spin" /> Importando...
                  </>
                ) : (
                  <>
                    <Download size={16} /> Importar Cuentas
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {renderTooltip()}
    </div>
  );
};

export default AccountsSection;
